/**
 * The bits of chrome the three documentation pages share: the tab strip, the download row and
 * the *Public docs* toggle (`specs/M8-P4-developers-docs-playground.md` §6).
 */
import { Toggle } from '@elizmonday/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as React from 'react';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import type { DevelopersOverview, DevelopersSource } from './source';

/** The injected source wins; production passes none and gets the tRPC one. */
export function useDevelopersSource(
  injected: DevelopersSource | undefined,
  fallback: DevelopersSource,
): DevelopersSource {
  return useMemo(() => injected ?? fallback, [injected, fallback]);
}

const TABS = [
  { id: 'reference', label: 'Reference', to: '/developers/docs' },
  { id: 'getting-started', label: 'Getting started', to: '/developers/docs/getting-started' },
  { id: 'migrating', label: 'Migrating from monday.com', to: '/developers/docs/migrating' },
] as const;

export function DocsTabs({ current }: { current: string }): React.ReactElement {
  const location = useLocation();
  return (
    <nav className="ez-docs__tabs" aria-label="API documentation">
      {TABS.map((tab) => (
        <Link
          key={tab.id}
          to={tab.to}
          className="ez-docs__tab"
          aria-current={tab.id === current ? 'page' : undefined}
          data-testid={`developers-tab-${tab.id}`}
          state={location.state}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

/**
 * The four files.
 *
 * Plain `<a download>` links, not fetch-and-blob: the routes are session-authenticated, the
 * browser sends the cookie, and a link the person can also right-click or paste into `curl` is
 * more useful than a button that hides the URL.
 */
export function DownloadsBar({
  overview,
  version,
}: {
  overview: DevelopersOverview;
  version?: string;
}): React.ReactElement {
  const suffix = version && version !== overview.defaultVersion ? `?version=${version}` : '';
  return (
    <>
      {overview.downloads.map((download) => (
        <a
          key={download.id}
          className="ez-docs__download"
          href={`${download.href}${suffix}`}
          download={download.name}
          title={download.description}
          data-testid={`developers-download-${download.id}`}
        >
          {download.name}
        </a>
      ))}
    </>
  );
}

/**
 * *Public docs* — the one admin switch on this section. Off by default; it opens the four files
 * to an anonymous reader and nothing else. A non-admin never sees it.
 */
export function PublicDocsToggle({
  overview,
  source,
}: {
  overview: DevelopersOverview;
  source: DevelopersSource;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => source.setPublicDocs(enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['developers', 'overview'] }),
  });
  if (!overview.canManagePublicDocs) return null;
  return (
    <label className="ez-docs__meta" data-testid="developers-public-docs">
      <Toggle
        isDefaultSelected={overview.publicDocs}
        isSelected={overview.publicDocs}
        onChange={(checked: boolean) => mutation.mutate(checked)}
        aria-label="Public docs"
        data-testid="developers-public-docs-toggle"
      />{' '}
      Public docs — let anyone read the reference and download the four files without signing in.
      <code> /v2</code> is unaffected.
    </label>
  );
}
